import { ArrowRightIcon } from "@minket/brand/icons/arrow-right";
import { Button } from "@minket/ui/components/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId } from "react";
import { BrandName } from "@/components/brands/named";
import { setSetupHidden, useSetupHidden } from "@/components/onboarding/hidden";
import { stepBrand, stepMarks } from "@/components/onboarding/progress";
import { Chain } from "@/components/page/chain";
import { useDocumentTitle } from "@/components/page/page";
import { firstOpenStep, isSetupComplete, summarize } from "@/lib/onboarding";
import { useSetupProgress } from "@/lib/progress";
import { type StepKey, setupSteps } from "@/lib/setup";

export const Route = createFileRoute("/onboarding/")({
  component: SetupStart,
});

/** What each step asks for, in one line: the chain says it once, so the steps need not repeat it. */
const asks: Record<StepKey, string> = {
  organization: "Its name and slug. You become its owner.",
  cloudflare: "A Cloudflare token, so Minket writes DNS records for you.",
  project: "One app's project, with production and preview environments.",
  domain: "A domain you control, verified with DKIM, SPF and DMARC.",
  keys: "A live key and a test key, each shown once.",
  test: "A test email, followed from accepted to delivered.",
  webhooks: "An endpoint that receives signed events.",
  inbound: "Mail received on your domain, as parsed JSON.",
  mailboxes: "Sending through Gmail, Microsoft 365 or any SMTP mailbox.",
  contacts: "An audience, with each contact's consent kept.",
};

/**
 * The setup flow's first page (direction §7, Setup: "the steps as a chain of custody (held,
 * skipped, waiting)"): every step on one chain, at the size of a page's centrepiece. A step done
 * is held, one skipped is struck, the next waits on a dashed knot, the rest are ahead. Each line
 * is the way into its step, with what it asks for and its saved state.
 */
function SetupStart() {
  useDocumentTitle("Setup");
  const headingId = useId();
  const progress = useSetupProgress();
  const marks = stepMarks(progress);
  const data = progress?.data;
  const open = data ? firstOpenStep(data) : null;
  const complete = data ? isSetupComplete(data) : false;
  const summary = data ? summarize(data) : null;
  const first = setupSteps[0];
  const hidden = useSetupHidden(data?.organization_id);
  return (
    <section aria-labelledby={headingId} className="grid min-w-0 content-start gap-8">
      <div className="flex min-w-0 flex-wrap items-end justify-between gap-x-10 gap-y-4 border-b border-seal-ink/30 pb-5">
        <div className="grid gap-1">
          <h2 id={headingId} className="type-heading text-[17px]">
            Steps done
          </h2>
          <p className="type-data text-[32px] leading-none tracking-[-0.01em] whitespace-nowrap">
            {summary ? summary.done : 0}
            <span className="text-muted-foreground"> of {setupSteps.length}</span>
          </p>
        </div>
        <Button asChild>
          {complete ? (
            <Link to="/">
              Every step is done: go to the overview
              <ArrowRightIcon size={20} aria-hidden />
            </Link>
          ) : (
            <Link to={open ? open.to : first.to}>
              {open ? `Continue: ${open.label}` : `Start with the ${first.label.toLowerCase()}`}
              <ArrowRightIcon size={20} aria-hidden />
            </Link>
          )}
        </Button>
      </div>
      {hidden && data && !complete ? (
        <p className="text-sm text-muted-foreground">
          Setup is hidden from the corner of other pages.{" "}
          <button
            type="button"
            onClick={() => setSetupHidden(data.organization_id, false)}
            className="cursor-pointer rounded-sm text-primary underline decoration-1 underline-offset-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Show it again
          </button>
        </p>
      ) : null}
      <Chain
        size="large"
        label="Setup steps"
        className="max-w-[52rem]"
        events={setupSteps.map((step) => {
          const mark = marks[step.key];
          return {
            id: step.key,
            knot: mark.knot,
            event: (
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-3">
                <Link
                  to={step.to}
                  className="rounded-sm text-foreground underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  <BrandName mark={stepBrand[step.key]}>{step.label}</BrandName>
                </Link>
                <span
                  className={
                    mark.knot === "waiting"
                      ? "text-[13px] font-semibold whitespace-nowrap text-foreground"
                      : "text-[13px] font-normal whitespace-nowrap text-muted-foreground"
                  }
                >
                  {mark.word}
                </span>
              </span>
            ),
            who: asks[step.key],
          };
        })}
      />
    </section>
  );
}
